import { Injectable, inject } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { MY_TOKEN_USAGE_BREAKDOWN_QUERY, MY_TOKEN_USAGE_QUERY } from './token-usage.graphql';

/**
 * DTO crudo tal cual lo devuelve el subgraph `langsmith` (TokenUsage).
 * Las fechas llegan como ISO string (scalar DateTime serializado a JSON).
 */
export interface TokenUsageDto {
  plan: string;
  maxTokens: number;
  usedTokens: number;
  percent: number;
  windowStart: string | null;
  windowEnd: string | null;
  active: boolean;
  lastSynced: string | null;
  exceeded: boolean;
}

interface MyTokenUsageResponse {
  myTokenUsage: TokenUsageDto | null;
}

/** Tokens del ciclo para un `app_feature` (ver token-usage.graphql.ts). */
export interface TokenUsageBreakdownItemDto {
  name: string;
  tokens: number;
}

interface MyTokenUsageBreakdownResponse {
  myTokenUsage: { breakdown: TokenUsageBreakdownItemDto[] | null } | null;
}

/**
 * Estado de la vista que consume el componente presentacional.
 * - `loading`: aún resolviendo la primera carga.
 * - `empty`: no hay ciclo activo (sin actividad reciente del usuario). El aro se
 *   pinta igual que `ready`, a 0% — no hay un tratamiento visual aparte.
 * - `ready`: hay un ciclo abierto con datos.
 * - `error`: fallo de red/servidor al resolver la query (estado degradado distinto de `empty`).
 */
export type UsageViewStatus = 'loading' | 'empty' | 'ready' | 'error';

/**
 * View-model ya formateado y listo para pintar. El componente no calcula nada:
 * solo lee estos campos. Toda la lógica vive en el mapper puro `mapToViewModel`.
 */
export interface UsageViewModel {
  status: UsageViewStatus;
  /** Porcentaje consumido, clamp 0..100, redondeado a entero para el aro/barra. */
  percent: number;
  /** Tokens usados formateados con separadores compactos (p.ej. "1.2M"). */
  usedLabel: string;
  /** Tope del plan formateado de forma compacta (p.ej. "5M"). */
  maxLabel: string;
  /** Hora local de renovación derivada de windowEnd (p.ej. "se renueva a las 18:42"). */
  resetAtLabel: string;
  /** Si hay ciclo activo (espejo de DTO.active). */
  active: boolean;
  /** usedTokens > maxTokens del plan. Informativo: no bloquea, solo avisa. */
  exceeded: boolean;
  /** Porciones del ciclo por apartado, desc por tokens. Vacío si no hay desglose. */
  breakdown: UsageBreakdownItemViewModel[];
}

/** Una porción del aro + fila de la leyenda, ya formateada. */
export interface UsageBreakdownItemViewModel {
  /** `app_feature` tal cual (clave estable para el track). */
  key: string;
  /** Clave i18n si el apartado es conocido; si no, se pinta `label`. */
  labelKey: string | null;
  /** Etiqueta legible derivada del nombre crudo, para apartados sin i18n. */
  label: string;
  /** Tokens del apartado, compactos (p.ej. "61.4K"). */
  tokensLabel: string;
  /** Peso sobre el total USADO del ciclo (la leyenda suma ~100%). */
  percent: number;
  /** Fracción sobre el TOPE del plan (0..1): longitud de su arco en el aro. */
  capFraction: number;
}

const LOADING_VIEW_MODEL: UsageViewModel = {
  status: 'loading',
  percent: 0,
  usedLabel: '0',
  maxLabel: '0',
  resetAtLabel: '',
  active: false,
  exceeded: false,
  breakdown: [],
};

const ERROR_VIEW_MODEL: UsageViewModel = {
  status: 'error',
  percent: 0,
  usedLabel: '0',
  maxLabel: '0',
  resetAtLabel: '',
  active: false,
  exceeded: false,
  breakdown: [],
};

/**
 * Servicio fino: una sola query `network-only` y un mapper puro DTO -> VM.
 * Mismo patrón Apollo que core/services/graphql.service.ts.
 */
@Injectable({ providedIn: 'root' })
export class TokenUsageService {
  private readonly apollo = inject(Apollo);

  /** View-model usado mientras se resuelve la primera carga. */
  readonly loadingViewModel = LOADING_VIEW_MODEL;

  /**
   * Carga el uso del usuario actual y lo mapea al view-model.
   * `network-only`: el contador debe reflejar el consumo real, no cachear.
   * Ante error de red/servidor devuelve un VM `error` (degradado limpio y
   * distinguible de `empty`, sin reventar Settings).
   */
  loadUsage(): Observable<UsageViewModel> {
    const usage$ = this.apollo
      .query<MyTokenUsageResponse>({
        query: MY_TOKEN_USAGE_QUERY,
        fetchPolicy: 'network-only',
      })
      .pipe(map(result => result.data?.myTokenUsage ?? null));

    // El desglose es accesorio: si falla, el aro se pinta sin porciones.
    const breakdown$ = this.apollo
      .query<MyTokenUsageBreakdownResponse>({
        query: MY_TOKEN_USAGE_BREAKDOWN_QUERY,
        fetchPolicy: 'network-only',
      })
      .pipe(
        map(result => {
          // errorPolicy 'all' (global): un fallo llega en `errors`, no lanza.
          if (result.errors?.length) {
            console.warn('[TokenUsage] el desglose por apartado no está disponible:', result.errors);
          }
          return result.data?.myTokenUsage?.breakdown ?? [];
        }),
        catchError(error => {
          console.warn('[TokenUsage] el desglose por apartado no está disponible:', error);
          return of([] as TokenUsageBreakdownItemDto[]);
        }),
      );

    return forkJoin([usage$, breakdown$]).pipe(
      map(([dto, breakdown]) => {
        const vm = mapToViewModel(dto);
        return vm.status === 'ready' ? { ...vm, breakdown: mapBreakdown(breakdown, dto?.maxTokens) } : vm;
      }),
      catchError(() => of(ERROR_VIEW_MODEL)),
    );
  }
}

// ─── Mapper puro (sin estado, fácilmente testeable) ──────────────────────

/**
 * Mapea el DTO del subgraph al view-model presentacional.
 * Función pura: misma entrada -> misma salida, sin efectos secundarios.
 */
export function mapToViewModel(dto: TokenUsageDto | null): UsageViewModel {
  if (!dto) {
    return {
      status: 'empty',
      percent: 0,
      usedLabel: '0',
      maxLabel: '0',
      resetAtLabel: '',
      active: false,
      exceeded: false,
      breakdown: [],
    };
  }

  const active = !!dto.active;
  const percent = clampPercent(dto.percent);

  return {
    status: active ? 'ready' : 'empty',
    percent,
    usedLabel: formatCompactTokens(dto.usedTokens),
    maxLabel: formatCompactTokens(dto.maxTokens),
    resetAtLabel: formatResetAtLabel(dto.windowEnd),
    active,
    exceeded: !!dto.exceeded,
    breakdown: [],
  };
}

/**
 * `app_feature` con traducción propia (ver agent.service.ts y
 * soc-chat-drawer.component.ts, que son quienes los ponen en la run). Un valor
 * nuevo no rompe nada: cae al heurístico de `formatFeatureName`.
 */
const KNOWN_APP_FEATURES = new Set(['soc_agent_chat', 'alerts_investigate', 'alerts_undo', 'sin_clasificar', 'otros']);

/**
 * Formatea el desglose para el aro y la leyenda. Función pura.
 * - `percent`: relativo al total usado (la leyenda suma ~100%).
 * - `capFraction`: relativo al tope del plan, para que los arcos juntos
 *   llenen el aro justo hasta el % consumido.
 */
export function mapBreakdown(
  items: TokenUsageBreakdownItemDto[] | null | undefined,
  maxTokens: number | null | undefined,
): UsageBreakdownItemViewModel[] {
  const list = (Array.isArray(items) ? items : [])
    .map(item => ({
      name: (item?.name ?? '').trim(),
      tokens: typeof item?.tokens === 'number' && Number.isFinite(item.tokens) ? Math.max(0, item.tokens) : 0,
    }))
    .filter(item => item.tokens > 0)
    .sort((a, b) => b.tokens - a.tokens);

  const total = list.reduce((sum, item) => sum + item.tokens, 0);
  const cap = typeof maxTokens === 'number' && maxTokens > 0 ? maxTokens : 0;
  // Con el tope superado, los arcos se reparten la vuelta entera en proporción.
  const capBase = Math.max(cap, total);

  return list.map(item => {
    const lower = item.name.toLowerCase();
    return {
      key: item.name || 'otros',
      labelKey: KNOWN_APP_FEATURES.has(lower) ? `settings.usage.features.${lower}` : null,
      label: formatFeatureName(item.name),
      tokensLabel: formatCompactTokens(item.tokens),
      percent: total > 0 ? Math.round((item.tokens / total) * 100) : 0,
      capFraction: capBase > 0 ? item.tokens / capBase : 0,
    };
  });
}

/** `alerts_remediate` -> "Alerts remediate". */
function formatFeatureName(name: string): string {
  const spaced = name.replace(/[_-]+/g, ' ').trim();
  if (!spaced) {
    return '—';
  }
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/** Asegura un porcentaje en el rango [0, 100], redondeado a entero. */
function clampPercent(value: number | null | undefined): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  return Math.min(100, Math.max(0, Math.round(n)));
}

/**
 * Formatea recuentos de tokens con sufijo compacto y separadores:
 * 1_200_000 -> "1.2M", 5_000_000 -> "5M", 14_200_000 -> "14.2M",
 * 2_400 -> "2.4K", 850 -> "850".
 */
function formatCompactTokens(value: number | null | undefined): string {
  const n = typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : 0;

  if (n >= 1_000_000) {
    return `${trimDecimal(n / 1_000_000)}M`;
  }
  if (n >= 1_000) {
    return `${trimDecimal(n / 1_000)}K`;
  }
  return String(Math.round(n));
}

/** Una sola decimal, sin el ".0" sobrante (1.0 -> "1", 1.25 -> "1.2"). */
function trimDecimal(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/**
 * Deriva la HORA de renovación (HH:MM) desde windowEnd en hora local.
 * Devuelve "" si no hay fecha (sin ciclo activo); el componente decide qué mostrar.
 */
function formatResetAtLabel(windowEnd: string | null | undefined): string {
  if (!windowEnd) {
    return '';
  }
  const date = new Date(windowEnd);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const hh = String(date.getHours()).padStart(2, '0');
  // Solo la hora: el texto que la envuelve ("se renueva a las …") lo pone el
  // template con la clave i18n `settings.usage.renewsAt`.
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}
